import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { RecruitingService } from 'src/app/api/recruiting.service';
import { PotentialTrip } from 'src/app/api/types/recruiting';
import { UiModule } from 'src/app/ui/ui.module';
import { RecruitingComponentsModule } from 'src/app/component-modules/recruiting-components/recruiting-components.module';
import { Contact, ContactsService, Ptrip, Customer, CustomerService, TripDetail, TripDetailService } from 'src/app/api';
import { PotentialTripService } from 'src/app/api/potential-trip.service';
import { BehaviorSubject, Observable, catchError, combineLatest, distinctUntilChanged, filter, map, of, switchMap, tap, Subscription } from 'rxjs';

@Component({
  selector: 'app-my-potential-trips',
  templateUrl: './my-potential-trips.component.html',
  styleUrls: ['./my-potential-trips.component.scss'],
  standalone: true,
  imports: [CommonModule, RouterModule, UiModule, RecruitingComponentsModule]
})
export class MyPotentialTripsComponent implements OnInit, OnDestroy {
  // State signals
  potentialTrips = signal<Ptrip[]>([]);
  contacts = signal<Contact[]>([]);
  customers = signal<Customer[]>([]);
  tripDetails = signal<TripDetail[]>([]);
  loading = signal<boolean>(false);
  error = signal<string>('');

  // Input with change detection
  private staffIdSubject = new BehaviorSubject<number>(0);
  @Input() set staffId(value: number) {
    this.staffIdSubject.next(value);
  }

  private subscriptions = new Subscription();

  constructor(
    private recruitingService: RecruitingService,
    private contactsService: ContactsService,
    private customerService: CustomerService,
    private tripDetailService: TripDetailService,
    private potentialTripService: PotentialTripService
  ) {}

  ngOnInit(): void {
    // Create a stream that reacts to staffId changes
    this.subscriptions.add(
      this.staffIdSubject.pipe(
        distinctUntilChanged(),
        filter(id => id > 0),
        tap(() => {
          this.loading.set(true);
          this.error.set('');
        }),
        switchMap(staffId => this.loadPotentialTripData(staffId)),
        catchError(err => {
          this.error.set('Failed to load potential trips data');
          this.loading.set(false);
          return of(null);
        })
      ).subscribe()
    );

    // Subscribe to potential trip updates to refresh the list
    this.subscriptions.add(
      this.potentialTripService.potentialTripUpdated$.subscribe(args => {
        const currentStaffId = this.staffIdSubject.value;
        if (currentStaffId > 0) {
          // Reload the data when a trip is added, updated, or removed
          this.loadPotentialTripData(currentStaffId).subscribe();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadPotentialTripData(staffId: number): Observable<void> {
    return this.recruitingService.getMyPotentialTrips(staffId).pipe(
      switchMap(trips => {
        if (!trips || trips.length === 0) {
          this.potentialTrips.set([]);
          this.contacts.set([]);
          this.customers.set([]);
          this.tripDetails.set([]);
          this.loading.set(false);
          return of(void 0);
        }
        const customerIds = [...new Set(trips.map(trip => trip.custId))];
        const contactIds = [...new Set(trips.map(trip => trip.contactId))];
        return combineLatest([
          of(trips),
          this.contactsService.getContactsFromIds(contactIds),
          this.customerService.getCustomersFromIds(customerIds),
          this.tripDetailService.getTripsForCustomerIds(customerIds)
        ]).pipe(
          tap(([trips, contacts, customers, tripDetails]) => {
            this.potentialTrips.set(trips);
            this.contacts.set(contacts);
            this.customers.set(customers);
            this.tripDetails.set(tripDetails);
            this.loading.set(false);
          }),
          map(() => void 0)
        );
      })
    );
  }

  getStatusClass(status: number): string {
    switch (status) {
      case 30:
        return 'text-info';
      case 50:
        return 'text-warning';
      case 75:
        return 'text-danger';
      case 100:
        return 'text-success';
      default:
        return '';
    }
  }

  isAssist(trip: PotentialTrip): boolean {
    return trip.assistID === this.staffIdSubject.value && trip.recruiterID !== this.staffIdSubject.value;
  }

  openLogActivityModal(custId: number): void {
    // TODO: Implement log activity modal
    console.log('Log activity for customer:', custId);
  }

  openScheduleActivityModal(custId: number): void {
    // TODO: Implement schedule activity modal
    console.log('Schedule activity for customer:', custId);
  }

  editPotentialTrip(id: number, custId: number): void {
    // TODO: Implement edit potential trip
    console.log('Edit potential trip:', id, custId);
  }

  generateRegiURL(id: number): void {
    // TODO: Implement generate registration URL
    console.log('Generate registration URL for trip:', id);
  }
} 